import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * useRedirectFallback — when a resource (product/article/category/page) returns
 * a 404, check the admin redirect map BEFORE showing the page's own "not found"
 * state. If a rule matches, navigate the browser to the target.
 *
 * Pages whose URLs match a React Router pattern (e.g. /product/:slug) never
 * fall through to the SPA's NotFound component, so without this hook they'd
 * silently skip the legacy-URL redirect lookup.
 *
 *   const enabled = Boolean(error);   // only run once we know it's a 404
 *   useRedirectFallback(enabled);
 */
export default function useRedirectFallback(enabled) {
  const location = useLocation();
  const triedRef = useRef(false);

  useEffect(() => {
    if (!enabled || triedRef.current) return;
    triedRef.current = true;
    const path = location.pathname + (location.search || "");
    api
      .get("/redirects/lookup", { params: { path } })
      .then((r) => {
        const target = r.data?.to_path;
        if (!target) return;
        if (target.startsWith("/")) {
          window.location.replace(target);
        } else {
          window.location.href = target;
        }
      })
      .catch(() => {
        /* no redirect rule — let the page show its own 404 UI */
      });
  }, [enabled, location.pathname, location.search]);
}
